import React from 'react';
import { TouchableOpacity, StyleSheet, ViewStyle, StyleProp, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../context/ThemeContext';
import { Typography } from './Typography';

interface BackButtonProps {
    /** Optional explicit handler. Defaults to navigation.goBack(). */
    onPress?: () => void;
    /** Optional label shown next to the chevron. */
    label?: string;
    /** Optional visual variant. `floating` adds an absolute-positioned chip
     *  with a glass background, used over photo-card hero screens. */
    variant?: 'inline' | 'floating';
    style?: StyleProp<ViewStyle>;
}

/**
 * Shared back button. Replaces the dozen ad-hoc chevron + goBack patterns
 * scattered through screens. Themed; respects RTL via Ionicons chevron-back.
 */
export const BackButton: React.FC<BackButtonProps> = ({
    onPress,
    label,
    variant = 'inline',
    style,
}) => {
    const { colors } = useTheme();
    const navigation = useNavigation();

    const handlePress = () => {
        if (onPress) {
            onPress();
            return;
        }
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
    };

    const isFloating = variant === 'floating';
    const wrapperStyle: StyleProp<ViewStyle> = [
        styles.wrapper,
        isFloating ? styles.floating : styles.inline,
        isFloating
            ? {
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                ...(Platform.OS === 'web'
                    ? ({ backdropFilter: 'blur(20px) saturate(180%)' } as object)
                    : {}),
            }
            : {},
        style,
    ];

    return (
        <TouchableOpacity
            onPress={handlePress}
            activeOpacity={0.7}
            style={wrapperStyle}
            accessibilityRole="button"
            accessibilityLabel={label ?? 'Назад'}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
            <Ionicons
                name="chevron-back"
                size={22}
                color={isFloating ? colors.charcoal : colors.textPrimary}
            />
            {label ? (
                <Typography
                    variant="button"
                    color={isFloating ? colors.charcoal : colors.textPrimary}
                    style={styles.label}
                >
                    {label}
                </Typography>
            ) : null}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    inline: {
        paddingVertical: 8,
        paddingRight: 8,
    },
    floating: {
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 30,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 100,
    },
    label: {
        marginLeft: 4,
    },
});
